/**
 * The Buildiary 1.1 Android app (github smazharhasan/site-office, app/app/index.tsx) opens the workspace in a
 * WebView and offers a bridge: the page posts {id, type, ...} with window.ReactNativeWebView.postMessage and the
 * app answers with window.BuildiaryNative.resolve(id, result). The same message shapes and results as 1.1's field
 * screens (buildiary/public/field/field.js):
 *   photo    {title, facing: 'back'|'front', lang} → {data: <base64 JPEG>, lat, lng, accuracy} | {cancelled: 1}
 *   scan     {title, lang}                        → {text} | {cancelled: 1}
 *   location {lang}                               → {lat, lng, accuracy}
 *   open     {url}                                → {ok: 1}   (WhatsApp, phone calls and other sites)
 *   forget   {}                                   → {ok: 1}   (the app asks for the workspace address again)
 *   ready / signout {lang}                        → {ok: 1}   (the app also takes the language from every message)
 *   any error                                     → {error: <message>}
 * Without the app (a browser), the web app uses the browser's camera, file picker and location as before.
 */
import type { Lang } from '@buildiary/core';

type Pending = { resolve: (v: Record<string, unknown>) => void; reject: (e: Error) => void };
type Bridge = { postMessage: (s: string) => void };
type W = Window & { ReactNativeWebView?: Bridge; BuildiaryNative?: { resolve?: (id: number, res: unknown) => void }; MimaarNative?: unknown };

const pending = new Map<number, Pending>();
let seq = 0;
let lang: Lang = 'en';

function win(): W | null {
  return typeof window === 'undefined' ? null : (window as W);
}

/** True inside the Buildiary Android app. */
export function isNative(): boolean {
  return !!win()?.ReactNativeWebView;
}

export function setNativeLang(l: Lang): void {
  lang = l;
}

function install(): void {
  const w = win();
  if (!w) return;
  w.BuildiaryNative = w.BuildiaryNative || {};
  w.BuildiaryNative.resolve = (id: number, res: unknown) => {
    const p = pending.get(id);
    if (!p) return;
    pending.delete(id);
    const r = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
    if (typeof r.error === 'string' && r.error) p.reject(new Error(r.error));
    else p.resolve(r);
  };
  // the Android app 1.0.0 answers under its earlier name
  w.MimaarNative = w.BuildiaryNative;
}
install();

/** One request to the app; resolves with its answer. */
export function nativeCall(type: string, data: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
  const w = win();
  if (!w?.ReactNativeWebView) return Promise.reject(new Error('Not in the Buildiary app'));
  install();
  return new Promise((resolve, reject) => {
    const id = ++seq;
    pending.set(id, { resolve, reject });
    w.ReactNativeWebView!.postMessage(JSON.stringify({ id, type, csrf: '', lang, ...data }));
  });
}

function b64ToFile(b64: string, name: string): File {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type: 'image/jpeg' });
}

export interface NativePhoto { file: File; lat: number | null; lng: number | null; accuracy: number | null }

/** A photo from the app's live camera (stamped with GPS), or null when the person cancelled. */
export async function nativePhoto(title: string, facing: 'back' | 'front' = 'back'): Promise<NativePhoto | null> {
  const r = await nativeCall('photo', { title, facing });
  if (r.cancelled || typeof r.data !== 'string' || !r.data) return null;
  const num = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' && v ? Number(v) : null);
  return { file: b64ToFile(r.data, `photo-${Date.now()}.jpg`), lat: num(r.lat), lng: num(r.lng), accuracy: num(r.accuracy) };
}

/** The text of a site card's QR code read by the app's scanner, or '' when cancelled. */
export async function nativeScan(title: string): Promise<string> {
  const r = await nativeCall('scan', { title });
  return typeof r.text === 'string' ? r.text : '';
}

export async function nativeLocation(): Promise<{ lat: number; lng: number; accuracy: number | null } | null> {
  const r = await nativeCall('location');
  const lat = Number(r.lat);
  const lng = Number(r.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng) ? { lat, lng, accuracy: typeof r.accuracy === 'number' ? r.accuracy : null } : null;
}

/** Opens a link outside the app (WhatsApp, calls, other sites); in a browser, a new tab. Returns false when blocked. */
export function openExternal(url: string): boolean {
  const w = win();
  if (!w) return false;
  if (isNative()) {
    let abs: URL | null = null;
    try {
      abs = new URL(url, w.location.href);
    } catch {
      abs = null;
    }
    if (abs && abs.origin === w.location.origin) w.location.href = abs.href;
    else nativeCall('open', { url: abs?.href ?? url }).catch(() => undefined);
    return true;
  }
  return !!w.open(url, '_blank', 'noopener');
}

/** In the app: the workspace address is asked again (1.1's "Change site address"). */
export function nativeForget(): void {
  nativeCall('forget').catch(() => undefined);
}

export function nativeNotify(type: 'ready' | 'signout'): void {
  if (isNative()) nativeCall(type).catch(() => undefined);
}

/**
 * In the app, links to other sites and target=_blank links go to the phone (WhatsApp, the browser) instead of
 * replacing the workspace in the WebView.
 */
export function installNativeLinks(): void {
  const w = win();
  if (!w || !isNative()) return;
  w.document.addEventListener('click', (e) => {
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a || e.defaultPrevented) return;
    const href = a.getAttribute('href') ?? '';
    if (!href || href.startsWith('#') || a.hasAttribute('download')) return;
    let url: URL;
    try {
      url = new URL(href, w.location.href);
    } catch {
      return;
    }
    const external = url.origin !== w.location.origin || /^(mailto|tel|whatsapp|intent|sms):/i.test(url.protocol);
    if (!external && a.target !== '_blank') return;
    e.preventDefault();
    openExternal(url.href);
  }, true);
}
